<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    /*
     * The bullets' tone is chosen by the background they land on, not by the component
     * hosting them: `light` for a dark background, `dark` for a light or saturated
     * colored one, and the default (null) for the page background. The pairing is decided
     * in Button.vue, which has the contrast measurement of each button color noted.
     */
    variant: {
      type: String,
      default: null,
      required: false,
      validator(value) {
        return [null, 'light', 'dark'].includes(value);
      },
    },
  });

  const classes = computed(() => ({
    'credere-spinner': true,
    'variant--light': props.variant === 'light',
    'variant--dark': props.variant === 'dark',
  }));
</script>

<template>
  <span :class="classes">
    <span
      v-for="i in 3"
      :key="i"
      class="credere-spinner-bullet"
    />
  </span>
</template>

<style lang="scss">
  .credere-spinner {
    --spinner-bullet: var(--cordel-spacing-mini);
    --spinner-margin: var(--spinner-bullet);

    // `inline` ignores width and height, and the box would measure zero under the absolute
    // bullets.
    display: inline-block;
    height: calc(var(--spinner-bullet) * 2);
    position: relative;
    width: calc(var(--spinner-bullet) * 3 + var(--spinner-margin) * 2);
  }

  @keyframes credere-spinner {
    0% { transform: translateY(calc(var(--spinner-bullet) / -2)); opacity: 1; }
    50% { transform: translateY(calc(var(--spinner-bullet) / 2)); opacity: 0.5; }
    100% { transform: translateY(calc(var(--spinner-bullet) / -2)); opacity: 1; }
  }

  .credere-spinner-bullet {
    --background-color: var(--cordel-color-neutral-primary);
    --border-radius: 100%;

    animation: credere-spinner 1s infinite;
    height: var(--spinner-bullet);
    left: 0;
    position: absolute;
    top: calc(50% - var(--spinner-bullet) / 4);
    width: var(--spinner-bullet);

    // The spinner's own class, never an ancestor's: a light form field carries the same name.
    .credere-spinner.variant--light & {
      --background-color: var(--cordel-color-constant-light);
    }

    .credere-spinner.variant--dark & {
      --background-color: var(--cordel-color-constant-dark);
    }

    // `nth-child` counts from 1; the position and the delay, from 0.
    @for $index from 1 through 3 {
      &:nth-child(#{$index}) {
        animation-delay: ($index - 1) * 0.2s;
        left: calc((var(--spinner-bullet) + var(--spinner-margin)) * #{$index - 1});
      }
    }
  }
</style>
